import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";

const ProjectContext = createContext({ projectId: null, project: null, loading: false });

export function ProjectContextProvider({ children }) {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!projectId) { setProject(null); return; }
    setLoading(true);
    api.get(`/projects/${projectId}`)
      .then((r) => setProject(r.data))
      .catch(() => setProject(null))
      .finally(() => setLoading(false));
  }, [projectId]);

  const value = useMemo(() => ({ projectId: projectId || null, project, loading }), [projectId, project, loading]);
  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

/** Returns the current project context.  { projectId, project, loading } */
export function useProjectContext() {
  return useContext(ProjectContext);
}

/** Convenience: returns just the current projectId or null. */
export function useProjectId() {
  return useContext(ProjectContext).projectId;
}

/** Helper: given an axios URL, append project_id if the current URL is under /p/:projectId. */
export function withProjectFilter(qs, projectId) {
  if (!projectId) return qs || "";
  const sep = qs && qs.includes("?") ? "&" : "?";
  return `${qs || ""}${sep}project_id=${projectId}`;
}

/** Small banner used inside project-mode pages to remind the user which project they are working in. */
export function ProjectBanner() {
  const { project, projectId } = useProjectContext();
  if (!projectId) return null;
  return (
    <div className="mb-3 flex items-center justify-between rounded-md border border-teal-200 bg-teal-50/60 px-3 py-2 text-xs" data-testid="project-banner">
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-wider text-teal-700">Project</div>
        <div className="truncate text-sm font-semibold text-teal-900">{project?.name || projectId}</div>
      </div>
      <Link to="/projects" className="rounded border border-teal-300 bg-white px-2 py-1 font-semibold text-teal-800 hover:bg-teal-100" data-testid="project-banner-switch">
        ← Switch project
      </Link>
    </div>
  );
}
